<template>
  <v-dialog v-model="dialog" transition="dialog-bottom-transition" max-width="500">
    <template v-slot:activator="{ on, attr }" v-if="!hideButton">
      <v-btn v-bind="attr" v-on="on" icon @click="initialize" data-test-upload-image-open>
        <v-icon>mdi-cloud-upload</v-icon>
      </v-btn>
    </template>
    <v-card flat>
      <v-form lazy-validation ref="form" v-model="valid">
        <v-card-title class="title pt-4" :class="color">
          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <h4 class="text-truncate">{{ $t("components.publicImage.PublicImageUploadDialog.title") }}</h4>
          <v-spacer />
          <v-btn @click="upload" :loading="isLoading" :disabled="!valid" data-test-upload-image-submit>
            {{ $t("components.publicImage.PublicImageUploadDialog.btnText") }}
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text>
          <v-select
            v-if="!hideFolderSelect"
            v-model="folderName"
            :items="imageFolderName"
            :label="$t('components.publicImage.PublicImageUploadDialog.folderNameText')"
            outlined
          />
          <v-file-input
            class="ma-4"
            v-model="file"
            :accept="imageInputTypes"
            @change="checkFileSize"
            :rules="[(v) => !!v || $t('report.thgContactData.mandatoryInput')]"
            show-size
            outlined
            data-test-upload-image-input
          />
        </v-card-text>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { requiredRule } from "@/lib/rules/requiredRule";
import DarkModeHighlightMixin from "@/mixins/DarkModeHighlightMixin.vue";
import { PublicImageFolderEnum } from "@/store/enum/public-image/publicImageFolderEnum";
import { PublicImageModule } from "@/store/modules/public.image.store";
import { Component, Prop } from "vue-property-decorator";
import { ConfigModule } from "@/store/modules/config";

@Component({
  components: {}
})
export default class PublicImageUploadDialog extends DarkModeHighlightMixin {
  EMIT_ON_UPLOAD = "onUpload";
  valid = false;

  dialog = false;
  isLoading = false;

  @Prop({ default: false })
  hideButton?: boolean;

  @Prop({ default: true })
  hideFolderSelect?: boolean;

  @Prop({ default: PublicImageFolderEnum.THG })
  folder!: PublicImageFolderEnum;

  /**
   * Max File Size in Mb
   */
  @Prop({ default: 2 })
  maxFileSize!: number;

  file: File | undefined = {} as any;

  folderName: PublicImageFolderEnum = PublicImageFolderEnum.THG;

  imageFolderName = Object.keys(PublicImageFolderEnum).map((k) => PublicImageFolderEnum[k as any]);

  @Prop({ default: "default" })
  prefix!: string;

  get imageInputTypes() {
    return ConfigModule.imageInputTypes;
  }

  get isDisabled() {
    return this.file !== undefined;
  }

  mounted() {
    this.folderName = this.folder || PublicImageFolderEnum.THG;
  }

  emitImage() {
    this.$emit(this.EMIT_ON_UPLOAD, PublicImageModule.publicImage);
  }

  async upload() {
    try {
      this.isLoading = true;
      if (this.file) {
        await PublicImageModule.upload({
          image: this.file,
          prefix: this.prefix,
          foldername: this.folderName
        });
      }
      this.emitImage();
    } catch (error) {
      this.$log.error(error);

      this.$toast.error(this.$t("components.publicImage.PublicImageUploadDialog.errorMessage"));
    } finally {
      this.isLoading = false;
      this.close();
    }
  }

  initialize() {
    this.dialog = true;
    this.file = undefined;
  }

  checkFileSize() {
    if (this.file) {
      if (this.file?.size > 1024 * 1000 * this.maxFileSize) {
        this.$toast.error(
          this.$t("components.publicImage.PublicImageUploadDialog.maxSizeMessage", { size: this.maxFileSize })
        );
      }
    }
  }

  get rules() {
    const rules = [];
    rules.push(requiredRule());
    return rules;
  }

  /**
   * closes the dialog
   */
  close() {
    this.file = undefined;
    this.dialog = false;
  }
}
</script>
